/* @flow */
import * as React from 'react';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

import PageWrapper from 'components/PageWrapper/PageWrapper';
import FormWrapper from 'components/FormWrapper/FormWrapper';
import FormSection from 'components/FormSection/FormSection';
import AutoCompleteData from 'models/forms/AutoCompleteModel.js';
import {
  autoCompleteForAllManufacturers,
  autoCompleteProducts,
  autoCompleteTradingAccountsForProductsEditForm,
} from 'api/forms.js';

import ElectroSelect from '../components/inputs/ElectroSelect/ElectroSelect';
import ElectroSelectExtra from '../components/inputs/ElectroSelectExtra/ElectroSelectExtra';
import ElectroTextField from '../components/inputs/ElectroTextField/ElectroTextField';
import ElectroResponseMessage from '../components/inputs/ElectroResponseMessage';

import ElectroNumberField from 'forms/components/inputs/ElectroNumberField/ElectroNumberField';
import getCurrencySymbol from 'utils/getCurrencySymbol';
import FormControls from '../components/controls/FormControls/FormControls';

import { makeFields, productCreate, productEdit } from './setupProductForm';
import AutoCompleteModel from '../../models/forms/AutoCompleteModel';
import ElectroSwitch from '../components/inputs/ElectroSwitch/ElectroSwitch';

type TypeProps = {|
  mode: string,
  itemData: Object,
|} & TranslatorProps;

@translate()
@observer
export default class ProductForm extends React.Component<*> {
  props: TypeProps;

  @observable accountsModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteTradingAccountsForProductsEditForm
  );

  @observable localState = {
    disableFields: false,
    responseMessage: '',
    showResponseMessage: false,
    isLoading: false,
    hasSuccess: false,
    hasError: false,
    selectedAccounts: '',
  };

  componentDidMount() {
    const itemData: Object = typeof this.props.itemData === 'undefined' ? {} : this.props.itemData;
    const isGlobalList: Boolean = itemData.global_list !== undefined ? itemData.global_list : true;

    this.localState.selectedAccounts = this.form.$('assignOtherAccounts.accounts').value;
    this.form.$('productDetail.globalList').set('value', isGlobalList);
  }

  @computed get form(): Object {
    const itemData: Object = typeof this.props.itemData === 'undefined' ? {} : this.props.itemData;
    const manufacturerID = this.props.manufacturerID ? this.props.manufacturerID : null;
    const shortCode = this.props.shortCode ? this.props.shortCode : null;
    const fields: Array<*> = makeFields(itemData, manufacturerID, shortCode, this.props.t);

    return this.props.mode === 'edit'
      ? productEdit(fields, itemData, this.localState, this.props.t)
      : productCreate(fields, this.localState, this.props.t);
  }

  updateFields() {
    const itemData: Object = typeof this.props.itemData === 'undefined' ? {} : this.props.itemData;
    const form: Object = this.form;
    const model = form.select('productDetail.model').value;
    const scode = form.select('productDetail.shortCode').value;

    if (model && scode) {
      form.update({
        productDetail: {
          baseModel: `${scode}${model}`,
        },
      });
    } else {
      form.update({
        productDetail: {
          baseModel: '',
        },
      });
    }

    // Set as Disabled
    if (itemData.status === 'Pending Review') {
      this.localState.disableFields = true;
    }
  }

  onResetFunc() {
    const itemData: Object = typeof this.props.itemData === 'undefined' ? {} : this.props.itemData;

    // Set as Disabled false
    if (itemData.status === 'Pending Review') {
      this.localState.disableFields = false;
    }
  }

  handleOnChange = (selectedAccounts: string) => {
    this.localState.selectedAccounts = selectedAccounts;
  };

  render(): React.Element<*> {
    const itemData: Object = typeof this.props.itemData === 'undefined' ? {} : this.props.itemData;
    const model: AutoCompleteData = new AutoCompleteData(autoCompleteForAllManufacturers);
    const modelProduct: AutoCompleteData = new AutoCompleteData(autoCompleteProducts);
    const t: Function = this.props.t;
    const activeUser: Object = JSON.parse(localStorage.getItem('user'));
    // Price formatting separators, checks for 1,234,567.89
    // This will need revisiting if any other formats are added
    const thousandSeparator: string = activeUser.price_format.match(/^\d+(,\d{3})*(\.\d{1,2})?$/g)
      ? ','
      : '.';
    const decimalSeparator: string = activeUser.price_format.match(/^\d+(,\d{3})*(\.\d{1,2})?$/g)
      ? '.'
      : ',';

    return (
      <PageWrapper isFlex={false}>
        <ElectroResponseMessage localState={this.localState} />
        <FormWrapper form={this.form}>
          <FormSection form={this.form} section="productDetail">
            <form>
              {this.props.mode === 'edit' ? (
                <React.Fragment>
                  <ElectroSelectExtra
                    field={this.form.$('productDetail.manufacturer')}
                    form={this.form}
                    formName="productDetail"
                    labelKey="name"
                    extraFilters={['aliases']}
                    updateCustomFields={() => this.updateFields()}
                    onResetFunc={() => this.onResetFunc()}
                    props={{
                      model,
                      modelChild: modelProduct,
                      itemData,
                    }}
                  />
                  <ElectroSelectExtra
                    field={this.form.$('productDetail.model')}
                    form={this.form}
                    formName="productDetail"
                    labelKey="model"
                    updateCustomFields={() => this.updateFields()}
                    onResetFunc={() => this.onResetFunc()}
                    props={{
                      model: modelProduct,
                      itemData,
                    }}
                  />
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <ElectroSelectExtra
                    field={this.form.$('productDetail.manufacturer')}
                    form={this.form}
                    updateCustomFields={() => this.updateFields()}
                    formName="productDetail"
                    labelKey="name"
                    extraFilters={['aliases']}
                    props={{
                      model,
                      addNewButton: {
                        path: '/manufacturers/create',
                        title: t('general:actions.new_manufacturer'),
                      },
                    }}
                  />
                  <ElectroTextField field={this.form.$('productDetail.model')} />
                </React.Fragment>
              )}
              {this.form.select('productDetail.shortCode').value ? (
                <ElectroTextField field={this.form.$('productDetail.baseModel')} disabled={true} />
              ) : null}
              <ElectroTextField
                field={this.form.$('productDetail.description')}
                disabled={this.localState.disableFields}
                props={{
                  multiline: true,
                  rows: 5,
                }}
              />
              <ElectroTextField
                field={this.form.$('productDetail.perfectProductNumber')}
                disabled={this.localState.disableFields}
              />
              <ElectroTextField
                field={this.form.$('productDetail.d365ProductNumber')}
                disabled={this.localState.disableFields}
              />
              <ElectroNumberField
                field={this.form.$('productDetail.fairMarketValue')}
                prefix={getCurrencySymbol('', activeUser.currency)}
                thousandSeparator={thousandSeparator}
                decimalSeparator={decimalSeparator}
                disabled={this.localState.disableFields}
              />

              {this.props.mode === 'edit' && (
                <ElectroSelect
                  field={this.form.$('productDetail.productStatus')}
                  searchable={false}
                  simpleValue={true}
                  disabled={this.localState.disableFields}
                  labelKey="label"
                  valueKey="id"
                />
              )}
              <ElectroSwitch field={this.form.$('productDetail.globalList')} />
            </form>
          </FormSection>
          <FormSection form={this.form} section="assignOtherAccounts">
            <ElectroSelectExtra
              field={this.form.$('assignOtherAccounts.accounts')}
              form={this.form}
              formName="assignOtherAccounts"
              labelKey={['name', 'account_no']}
              multi={true}
              searchable={true}
              handleOnChange={this.handleOnChange}
              singleSelectedValue={this.localState.selectedAccounts}
              labelSeparator=" - "
              props={{
                model: this.accountsModel,
              }}
            />
          </FormSection>
          <FormControls
            form={this.form}
            buttonText={
              this.props.mode === 'edit' ? t('general:actions.update') : t('general:actions.create')
            }
          />
        </FormWrapper>
      </PageWrapper>
    );
  }
}
